import { createRoot } from 'react-dom/client';
import App from './App';
import { StrictMode, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DeviceProvider } from './utils/device/DeviceProvider';
import ErrorFallback from './components/ErrorFallback';
import { QueryError } from './utils/errors/QueryError';
import { MutationError } from './utils/errors/MutationError';
import { ToastContainer, Zoom } from 'react-toastify';
import { loadRuntimeConfig } from './utils/runtimeConfig';
import { initSentry } from './sentry';
import { MsalProvider } from '@azure/msal-react';
import { getMsalInstance } from './auth/msalInstanceManager';
import { getAuthProvider, isAuthDisabled } from './auth/authConfig';
import { MosError } from './utils/errors/MosError';
import { handleSessionTermination } from './utils/errors/sessionTerminationHandler';
import { registerQueryClient } from './utils/queryClientRegistry';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (error, query) => {
        // Check for token invalidation error
        if (
          error instanceof MosError &&
          error.errorCode === 'FORBIDDEN' &&
          error.message === 'Token has been invalidated'
        ) {
          handleSessionTermination(queryClient).catch((err) => {
            console.error('Error during session termination:', err);
          });
        }
        new QueryError(error?.message, query).capture();
        return false;
      },
    },
    mutations: {
      onError: (error, mutationVariables) => {
        // Check for token invalidation error
        if (
          error instanceof MosError &&
          error.errorCode === 'FORBIDDEN' &&
          error.message === 'Token has been invalidated'
        ) {
          handleSessionTermination(queryClient).catch((err) => {
            console.error('Error during session termination:', err);
          });
        }
        new MutationError(error?.message, mutationVariables).capture();
      },
    },
  },
});

// Register queryClient for global access (e.g., from HTTP error handlers)
registerQueryClient(queryClient);

/**
 * useMsal() falls back to a stub (empty accounts) when used outside MsalProvider.
 * AuthenticatedApp and other ancestors call useAuth → useEntraAuth before EntraAuthProvider
 * mounts, so Entra ID requires a single provider above the router.
 */
function wrapWithEntraMsalIfNeeded(children: ReactNode): ReactNode {
  try {
    if (!isAuthDisabled() && getAuthProvider() === 'entra') {
      return (
        <MsalProvider instance={getMsalInstance()}>{children}</MsalProvider>
      );
    }
  } catch (error) {
    console.error(
      'Failed to initialize Microsoft authentication context:',
      error,
    );
  }
  return children;
}

async function bootstrap() {
  await loadRuntimeConfig();
  initSentry();

  const appShell = (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      onError={(error) => {
        console.warn('Error caught by boundary:', error);
      }}
    >
      <DeviceProvider>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <App />
            <ToastContainer
              position="top-right"
              autoClose={2000}
              hideProgressBar={true}
              closeOnClick
              draggable={false}
              pauseOnHover
              theme="colored"
              transition={Zoom}
            />
          </QueryClientProvider>
        </BrowserRouter>
      </DeviceProvider>
    </Sentry.ErrorBoundary>
  );

  createRoot(document.getElementById('root')!, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(<StrictMode>{wrapWithEntraMsalIfNeeded(appShell)}</StrictMode>);
}

bootstrap();
